import { EXCHANGE_REASON_LABELS, type ExchangeSummary } from '@aeris/contracts'

import {
  dateValue,
  numberValue,
  textValue,
  type ExportColumn,
} from './export/table-export.ts'

/**
 * O que vai no arquivo quando se exporta Trocas — F158.
 *
 * A tela mostra três seções e nenhuma coluna de motivo; o arquivo leva **tudo
 * o que a troca guarda**, inclusive o que a lista nunca teve espaço para
 * mostrar: o motivo do pedido, a observação do trocador e o motivo da recusa.
 *
 * **O arquivo não traz as PEÇAS.** A rota de lista devolve o cabeçalho da
 * troca, e uma linha por peça exigiria buscar cada troca uma a uma — cem
 * pedidos para exportar cem trocas, que é exatamente o N+1 que a seção 27
 * proíbe. Quem precisa da peça abre o comprovante. Quando o relatório de trocas
 * chegar (fatia 6), é ele que responde "qual peça volta mais".
 */

type Column = ExportColumn<ExchangeSummary>

const KIND_LABELS: Record<ExchangeSummary['kind'], string> = {
  exchange: 'Troca',
  voucher: 'Vale',
}

const STATUS_LABELS: Record<ExchangeSummary['status'], string> = {
  requested: 'Pedida',
  accepted: 'Aceita',
  rejected: 'Recusada',
  completed: 'Concluída',
  cancelled: 'Cancelada',
}

/** Vazio enquanto está só pedida: o número nasce quando a troca se conclui. */
const numberOf = (exchange: ExchangeSummary): string =>
  exchange.number === null ? '' : String(exchange.number)

/**
 * A venda de origem, ou a marca de que não houve venda (F148).
 *
 * "—" seria ambíguo: quem lê a planilha não distingue "não teve venda" de
 * "ninguém preencheu".
 */
const origin = (exchange: ExchangeSummary): string =>
  exchange.originalSaleNumber === null
    ? 'gestão antiga (produto de fora)'
    : `${exchange.originalSaleKind === 'order' ? 'pedido' : 'venda'} nº ${String(exchange.originalSaleNumber)}`

export const EXCHANGE_EXPORT: readonly Column[] = [
  { id: 'number', label: 'Nº', value: numberOf },
  {
    id: 'completedAt',
    label: 'Concluída em',
    value: (e) => dateValue(e.completedAt),
  },
  {
    id: 'requestedAt',
    label: 'Pedida em',
    value: (e) => dateValue(e.requestedAt),
  },
  { id: 'customerName', label: 'Cliente', value: (e) => e.customerName },
  { id: 'kind', label: 'Tipo', value: (e) => KIND_LABELS[e.kind] },
  { id: 'status', label: 'Situação', value: (e) => STATUS_LABELS[e.status] },
  { id: 'origin', label: 'Origem', value: origin },
  {
    id: 'returnedTotal',
    label: 'Devolvido',
    value: (e) => numberValue(e.returnedTotal),
  },
  {
    id: 'outgoingTotal',
    label: 'Levou',
    value: (e) => numberValue(e.outgoingTotal),
  },
  {
    id: 'voucherCreditAmount',
    label: 'Vale gerado',
    value: (e) => numberValue(e.voucherCreditAmount),
  },
  {
    id: 'differenceAmount',
    label: 'Diferença',
    value: (e) => numberValue(e.differenceAmount),
  },
  {
    id: 'requestedByName',
    label: 'Pedida por',
    value: (e) => textValue(e.requestedByName),
  },
  {
    id: 'handledByName',
    label: 'Atendida por',
    value: (e) => textValue(e.handledByName),
  },
  {
    id: 'authorisedByName',
    label: 'Autorizada por',
    value: (e) => textValue(e.authorisedByName),
  },
  /*
   * O motivo que se CONTA, antes do que se lê (F158).
   *
   * Sai como o rótulo, não como o código: `nao_serviu` numa planilha é uma
   * coluna que alguém tem de traduzir à mão antes de mostrar a alguém.
   */
  {
    id: 'reasonCode',
    label: 'Motivo',
    value: (e) =>
      e.reasonCode === null ? '' : EXCHANGE_REASON_LABELS[e.reasonCode],
  },
  // Os três textos que a tela não tem onde mostrar.
  {
    id: 'reason',
    label: 'Detalhe do motivo',
    value: (e) => textValue(e.reason),
  },
  { id: 'note', label: 'Observação', value: (e) => textValue(e.note) },
  {
    id: 'rejectionReason',
    label: 'Motivo da recusa',
    value: (e) => textValue(e.rejectionReason),
  },
  { id: 'branchName', label: 'Loja', value: (e) => e.branchName },
]
